
import { defineComponent, ref, onMounted } from "vue";
import ApiService from "@/core/services/ApiService";
import { Endpoints } from "@/api/ApiEndpoints";

export default defineComponent({
  name: "GoalTimingPage",
  setup() {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const asInputDate = (d: Date) => d.toISOString().slice(0, 10);

    const from = ref(asInputDate(weekAgo));
    const to = ref(asInputDate(today));
    const userId = ref<string>("");
    // Won by default: a coupon that lost cost nothing, so it is not what we are hunting.
    const statusId = ref<string>("1");
    const thresholdSeconds = ref(180);
    const flaggedOnly = ref(true);
    // First-half bets are the trustworthy ones: before the interval the match clock has not
    // yet drifted from kickoff by a half-time of unknown length.
    const firstHalfOnly = ref(true);

    const loading = ref(false);
    const report = ref<any>(null);

    const load = async () => {
      loading.value = true;
      try {
        const params: Record<string, unknown> = {
          from: from.value,
          // include the whole end day, the API compares against CreatedAt
          to: to.value + "T23:59:59",
          thresholdSeconds: thresholdSeconds.value,
          flaggedOnly: flaggedOnly.value,
          firstHalfOnly: firstHalfOnly.value,
        };
        if (userId.value) {
          params.userId = Number(userId.value);
        }
        // "" means all statuses - omit the param entirely so the API treats it as null,
        // and note that "0" (open) is a real value that must not be dropped as falsy.
        if (statusId.value !== "") {
          params.statusId = Number(statusId.value);
        }
        const res = await ApiService.get(Endpoints.COUPON_GOAL_TIMING, "", params);
        report.value = res?.data ?? null;
      } catch (e) {
        console.error("Failed to load goal timing report", e);
        report.value = null;
      }
      loading.value = false;
    };

    const formatDateTime = (value: string | null) => {
      if (!value) return "—";
      const d = new Date(value);
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ` +
        `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    };

    const formatGap = (seconds: number) => {
      if (seconds < 60) return `${seconds}s`;
      const mins = Math.floor(seconds / 60);
      const rest = seconds % 60;
      if (mins < 60) return rest === 0 ? `${mins}m` : `${mins}m ${rest}s`;
      const hours = Math.floor(mins / 60);
      return `${hours}h ${mins % 60}m`;
    };

    // Tighter than the threshold reads as worse, so the colour tracks the gap not just the flag.
    const gapTextClass = (seconds: number) => {
      if (seconds <= 60) return "text-danger";
      if (seconds <= thresholdSeconds.value) return "text-warning";
      return "";
    };

    const isSuspiciousGoal = (goal: any) =>
      goal.secondsToBet !== null && goal.secondsToBet >= 0 && goal.secondsToBet <= thresholdSeconds.value;

    const couponBorderClass = (coupon: any) => {
      if (coupon.minSecondsAfterGoal === null) return "";
      if (coupon.minSecondsAfterGoal <= 60) return "border-danger";
      if (coupon.minSecondsAfterGoal <= thresholdSeconds.value) return "border-warning";
      return "";
    };

    const statusText = (statusId: number | null) => {
      if (statusId === 1) return "Won";
      if (statusId === 2) return "Lost";
      return "Open";
    };

    const statusBadgeClass = (statusId: number | null) => {
      if (statusId === 1) return "badge-light-success";
      if (statusId === 2) return "badge-light-danger";
      return "badge-light";
    };

    onMounted(() => load());

    return {
      from,
      to,
      userId,
      statusId,
      thresholdSeconds,
      flaggedOnly,
      firstHalfOnly,
      loading,
      report,
      load,
      formatDateTime,
      formatGap,
      gapTextClass,
      isSuspiciousGoal,
      couponBorderClass,
      statusText,
      statusBadgeClass,
    };
  },
});
